import { computed } from "vue";
import { useSupplementLabelStore } from "../../stores/supplement-label-store";
import { preventScientificNotation } from "../utils/formatting";
import { Flash } from "~/jose_modern/flash";

/**
 * Composable for list item operations (lines, dvLines, blends)
 * Handles move, remove, and update operations directly with the store
 */
export function useListItem(listName, uid) {
  const store = useSupplementLabelStore();

  // Direct store property access in computed for proper Vue reactivity tracking
  const list = computed(() => {
    if (listName === "lines") return store.linesWithoutDv;
    if (listName === "dvLines") return store.dvLines;
    if (listName === "blends") return store.blends;
    return [];
  });

  const listConfig = {
    lines: {
      move: (from, to) => store.moveLine(from, to),
      remove: (index) => store.removeLine(index),
    },
    dvLines: {
      move: (from, to) => store.moveDvLine(from, to),
      remove: (index) => store.removeDvLine(index),
    },
    blends: {
      move: (from, to) => store.moveBlend(from, to),
      remove: (index) => store.removeBlend(index),
    },
  };

  const config = listConfig[listName];
  if (!config) {
    throw new Error(`Unknown list: ${listName}`);
  }

  const index = computed(() => list.value.findIndex((item) => item._uid === uid.value));

  const item = computed(() => list.value.find((item) => item._uid === uid.value));

  const totalItems = computed(() => list.value.length);

  const canMoveUp = computed(() => index.value > 0);

  const canMoveDown = computed(() => index.value >= 0 && index.value < list.value.length - 1);

  function update(updates) {
    if (item.value) {
      Object.assign(item.value, updates);
    }
  }

  async function moveUp() {
    if (!canMoveUp.value) return;
    config.move(index.value, index.value - 1);
    if (config.afterMove) {
      await config.afterMove();
    }
  }

  async function moveDown() {
    if (!canMoveDown.value) return;
    config.move(index.value, index.value + 1);
    if (config.afterMove) {
      await config.afterMove();
    }
  }

  async function remove() {
    const currentIndex = index.value;
    if (currentIndex < 0) return;

    if (config.beforeRemove) {
      try {
        await config.beforeRemove(item.value);
      } catch (e) {
        Flash.error(e.message);
        return;
      }
    }

    config.remove(currentIndex);
    return currentIndex;
  }

  return {
    store,
    item,
    index,
    totalItems,
    canMoveUp,
    canMoveDown,
    update,
    moveUp,
    moveDown,
    remove,
    preventScientificNotation,
  };
}
